import React, { useEffect, useRef, useState } from 'react';

const FOOD_EMOJIS = ['🥗', '🍕', '🥩', '🥦', '🍎', '🍋', '🫐', '🌽', '🥕', '🍓', '🧁', '🥑', '🍜', '🫕', '🧆'];

function FloatingEmoji({ emoji, style }) {
  return (
    <div style={{
      position: 'absolute',
      fontSize: style.size,
      opacity: style.opacity,
      animation: `floatUp ${style.duration}s ease-in-out infinite`,
      animationDelay: style.delay,
      left: style.left,
      bottom: '-80px',
      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))',
      pointerEvents: 'none',
      zIndex: 2,
    }}>
      {emoji}
    </div>
  );
}

function StatCard({ value, label, icon, delay }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const animated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animated.current) {
        animated.current = true;
        let start = 0;
        const duration = 1800;
        const step = 16;
        const increment = value / (duration / step);
        const timer = setInterval(() => {
          start += increment;
          if (start >= value) { setCount(value); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, step);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div ref={ref} className="stat-card" style={{ animationDelay: delay }}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-value">{count.toLocaleString()}{value >= 1000 ? '+' : '%'}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function TestimonialCard({ quote, name, role, avatar, delay }) {
  return (
    <div className="testimonial-card" style={{ animationDelay: delay }}>
      <div className="quote-mark">"</div>
      <p className="testimonial-text">{quote}</p>
      <div className="testimonial-footer">
        <div className="testimonial-avatar">{avatar}</div>
        <div>
          <div className="testimonial-name">{name}</div>
          <div className="testimonial-role">{role}</div>
        </div>
      </div>
      <div className="star-row">{'⭐'.repeat(5)}</div>
    </div>
  );
}

function LandingPage({ onStart, isFading }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const floatingItems = FOOD_EMOJIS.map((emoji, i) => ({
    emoji,
    style: {
      size: `${Math.random() * 24 + 22}px`,
      opacity: Math.random() * 0.35 + 0.12,
      duration: Math.random() * 8 + 10,
      delay: `${Math.random() * -18}s`,
      left: `${(i / FOOD_EMOJIS.length) * 100}%`,
    }
  }));

  return (
    <div style={{
      fontFamily: "'Nunito', 'Comfortaa', cursive",
      color: '#1a1a2e',
      overflowY: 'auto',
      height: '100vh',
      backgroundColor: '#0f0c29',
      display: 'flex',
      flexDirection: 'column',
      opacity: isFading ? 0 : 1,
      transition: 'opacity 0.6s ease-in-out',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;600;700;800;900&family=Poppins:wght@400;600;700;800&display=swap');

        * { box-sizing: border-box; }

        @keyframes floatUp {
          0% { transform: translateY(0) rotate(0deg) scale(1); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 0.8; }
          100% { transform: translateY(-110vh) rotate(360deg) scale(0.8); opacity: 0; }
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes heroImageFloat {
          0%, 100% { transform: translateY(0px) rotate(-1deg); }
          50% { transform: translateY(-14px) rotate(1deg); }
        }

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255, 140, 0, 0.4); }
          50% { box-shadow: 0 0 0 16px rgba(255, 140, 0, 0); }
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        @keyframes rotateSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes bounceDot {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }

        .hero-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          background: linear-gradient(135deg, #0f0c29 0%, #302b63 40%, #24243e 70%, #1a1a3e 100%);
          background-size: 400% 400%;
          animation: gradientShift 12s ease infinite;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 30% 50%, rgba(255,140,0,0.08) 0%, transparent 60%),
                      radial-gradient(ellipse at 80% 20%, rgba(100,200,255,0.07) 0%, transparent 50%);
          z-index: 1;
        }

        .hero-grid-pattern {
          position: absolute;
          inset: 0;
          background-image: linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          z-index: 1;
        }

        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.4s ease;
          padding: 0 5%;
        }

        .navbar.scrolled {
          background: rgba(15, 12, 41, 0.92);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          box-shadow: 0 4px 30px rgba(0,0,0,0.3);
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .navbar-inner {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 0;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
        }

        .logo-icon-wrap {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #ff8c00, #ff5733);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          box-shadow: 0 4px 12px rgba(255,140,0,0.4);
          flex-shrink: 0;
        }

        .logo-text {
          font-family: 'Poppins', sans-serif;
          font-size: 22px;
          font-weight: 800;
          background: linear-gradient(90deg, #ff8c00, #ffcc44);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -0.5px;
        }

        .nav-cta {
          background: linear-gradient(135deg, #ff8c00, #ff5c00);
          color: #fff;
          border: none;
          padding: 12px 28px;
          border-radius: 50px;
          font-size: 15px;
          cursor: pointer;
          font-weight: 700;
          font-family: 'Nunito', sans-serif;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(255,140,0,0.35);
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .nav-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(255,140,0,0.5);
          background: linear-gradient(135deg, #ff9f1a, #ff6b1a);
        }

        .hero-content {
          position: relative;
          z-index: 3;
          max-width: 1200px;
          margin: 0 auto;
          padding: 120px 5% 80px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
          width: 100%;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,140,0,0.15);
          border: 1px solid rgba(255,140,0,0.3);
          color: #ffb347;
          padding: 8px 18px;
          border-radius: 50px;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 24px;
          animation: fadeSlideUp 0.6s ease forwards;
          backdrop-filter: blur(10px);
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .hero-title {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(36px, 4.5vw, 64px);
          font-weight: 800;
          color: #fff;
          line-height: 1.15;
          margin: 0 0 24px 0;
          animation: fadeSlideUp 0.7s ease forwards;
          animation-delay: 0.1s;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        .hero-title .highlight {
          background: linear-gradient(90deg, #ff8c00, #ffcc44, #ff8c00);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }

        .hero-subtitle {
          font-size: clamp(16px, 1.8vw, 20px);
          color: rgba(255,255,255,0.72);
          line-height: 1.7;
          margin: 0 0 40px 0;
          animation: fadeSlideUp 0.8s ease forwards;
          animation-delay: 0.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        .hero-actions {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          animation: fadeSlideUp 0.9s ease forwards;
          animation-delay: 0.3s;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        .cta-primary {
          background: linear-gradient(135deg, #ff8c00, #ff5733);
          color: #fff;
          border: none;
          padding: 18px 38px;
          border-radius: 50px;
          font-size: 18px;
          cursor: pointer;
          font-weight: 800;
          font-family: 'Nunito', sans-serif;
          transition: all 0.3s ease;
          box-shadow: 0 8px 25px rgba(255,140,0,0.45);
          display: inline-flex;
          align-items: center;
          gap: 10px;
          animation: pulseGlow 2.5s ease infinite;
        }

        .cta-primary:hover {
          transform: translateY(-3px) scale(1.03);
          box-shadow: 0 16px 35px rgba(255,140,0,0.55);
        }

        .cta-secondary {
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.9);
          border: 1px solid rgba(255,255,255,0.2);
          padding: 18px 32px;
          border-radius: 50px;
          font-size: 17px;
          cursor: pointer;
          font-weight: 700;
          font-family: 'Nunito', sans-serif;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          backdrop-filter: blur(10px);
        }

        .cta-secondary:hover {
          background: rgba(255,255,255,0.15);
          transform: translateY(-2px);
        }

        .hero-trust {
          margin-top: 36px;
          display: flex;
          align-items: center;
          gap: 14px;
          color: rgba(255,255,255,0.5);
          font-size: 13px;
          animation: fadeSlideUp 1s ease forwards;
          animation-delay: 0.45s;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        .trust-avatars {
          display: flex;
        }

        .trust-avatar {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.3);
          margin-left: -8px;
          font-size: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #302b63, #24243e);
        }

        .trust-avatar:first-child { margin-left: 0; }

        .hero-image-wrap {
          position: relative;
          z-index: 3;
          animation: fadeSlideUp 1s ease forwards;
          animation-delay: 0.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        .hero-image-card {
          position: relative;
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1);
          animation: heroImageFloat 6s ease-in-out infinite;
        }

        .hero-image-card img {
          width: 100%;
          height: 420px;
          object-fit: cover;
          display: block;
        }

        .hero-image-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(15,12,41,0.5) 0%, transparent 60%);
        }

        .hero-badge-float {
          position: absolute;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          padding: 12px 18px;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          font-family: 'Nunito', sans-serif;
          font-weight: 700;
          color: #1a1a2e;
          font-size: 13px;
          animation: heroImageFloat 5s ease-in-out infinite;
        }

        .hero-badge-float.top-left {
          top: 20px;
          left: -20px;
          animation-delay: -2s;
        }

        .hero-badge-float.bottom-right {
          bottom: 20px;
          right: -20px;
          animation-delay: -4s;
        }

        .badge-dot {
          width: 8px;
          height: 8px;
          background: #22c55e;
          border-radius: 50%;
          animation: bounceDot 1.5s ease-in-out infinite;
          flex-shrink: 0;
        }

        /* Stats Section */
        .stats-section {
          background: linear-gradient(135deg, #ff8c00, #ff5733);
          padding: 60px 5%;
          display: flex;
          justify-content: center;
        }

        .stats-grid {
          max-width: 1100px;
          width: 100%;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }

        .stat-card {
          text-align: center;
          color: #fff;
          padding: 20px;
          animation: fadeSlideUp 0.6s ease both;
        }

        .stat-icon { font-size: 36px; margin-bottom: 10px; }
        .stat-value {
          font-family: 'Poppins', sans-serif;
          font-size: 42px;
          font-weight: 800;
          line-height: 1;
          margin-bottom: 6px;
        }
        .stat-label { font-size: 14px; opacity: 0.85; font-weight: 600; letter-spacing: 0.3px; }

        /* How It Works */
        .how-section {
          background: #f8f9ff;
          padding: 100px 5%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .section-eyebrow {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #ff8c00;
          margin-bottom: 14px;
        }

        .section-title {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(28px, 3.5vw, 44px);
          font-weight: 800;
          color: #1a1a2e;
          text-align: center;
          margin: 0 0 16px 0;
          line-height: 1.25;
        }

        .section-subtitle {
          font-size: 18px;
          color: #6b7280;
          text-align: center;
          max-width: 560px;
          line-height: 1.7;
          margin-bottom: 70px;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 40px;
          max-width: 1100px;
          width: 100%;
          position: relative;
        }

        .steps-grid::before {
          content: '';
          position: absolute;
          top: 50px;
          left: calc(16.66% + 20px);
          right: calc(16.66% + 20px);
          height: 2px;
          background: linear-gradient(90deg, #ff8c00, #ffcc44, #ff8c00);
          background-size: 200%;
          animation: shimmer 3s linear infinite;
          z-index: 0;
        }

        .step-card {
          background: #fff;
          border-radius: 24px;
          padding: 40px 32px;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06);
          transition: all 0.35s ease;
          position: relative;
          z-index: 1;
          border: 1px solid rgba(0,0,0,0.05);
        }

        .step-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(255,140,0,0.15);
          border-color: rgba(255,140,0,0.2);
        }

        .step-number-wrap {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #ff8c00, #ff5733);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          font-size: 38px;
          box-shadow: 0 8px 20px rgba(255,140,0,0.3);
        }

        .step-title {
          font-family: 'Poppins', sans-serif;
          font-size: 20px;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 12px;
        }

        .step-desc { color: #6b7280; line-height: 1.7; font-size: 15px; }

        .step-tag {
          display: inline-block;
          background: #fff5e6;
          color: #ff8c00;
          font-size: 11px;
          font-weight: 700;
          padding: 4px 12px;
          border-radius: 20px;
          margin-top: 16px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        /* Feature Section */
        .features-section {
          background: #fff;
          padding: 100px 5%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 28px;
          max-width: 1100px;
          width: 100%;
        }

        .feature-card {
          background: linear-gradient(145deg, #f8f9ff, #fff);
          border: 1px solid #e8eaf6;
          border-radius: 22px;
          padding: 36px 30px;
          transition: all 0.35s ease;
          position: relative;
          overflow: hidden;
        }

        .feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #ff8c00, #ffcc44);
          transform: scaleX(0);
          transition: transform 0.35s ease;
          transform-origin: left;
        }

        .feature-card:hover::before { transform: scaleX(1); }
        .feature-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.1);
          border-color: rgba(255,140,0,0.2);
        }

        .feature-icon-wrap {
          width: 64px;
          height: 64px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          margin-bottom: 22px;
          position: relative;
        }

        .feature-title {
          font-family: 'Poppins', sans-serif;
          font-size: 20px;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 12px;
        }

        .feature-desc { color: #6b7280; line-height: 1.7; font-size: 15px; }

        /* Gallery Strip */
        .gallery-section {
          background: #0f0c29;
          padding: 80px 5%;
          overflow: hidden;
        }

        .gallery-inner {
          max-width: 1100px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          align-items: center;
        }

        .gallery-text { color: #fff; }
        .gallery-image-wrap {
          border-radius: 24px;
          overflow: hidden;
          position: relative;
          animation: heroImageFloat 7s ease-in-out infinite;
          box-shadow: 0 30px 70px rgba(0,0,0,0.5);
        }

        .gallery-image-wrap img {
          width: 100%;
          height: 360px;
          object-fit: cover;
          display: block;
        }

        .gallery-image-wrap::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 24px;
          border: 1px solid rgba(255,255,255,0.1);
        }

        /* Testimonials */
        .testimonials-section {
          background: #f8f9ff;
          padding: 100px 5%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          max-width: 1100px;
          width: 100%;
        }

        .testimonial-card {
          background: #fff;
          border-radius: 20px;
          padding: 32px 28px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06);
          border: 1px solid rgba(0,0,0,0.05);
          transition: all 0.35s ease;
          position: relative;
          overflow: hidden;
          animation: fadeSlideUp 0.6s ease both;
        }

        .testimonial-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.12);
        }

        .quote-mark {
          font-family: Georgia, serif;
          font-size: 72px;
          color: #ff8c00;
          opacity: 0.2;
          line-height: 1;
          margin-bottom: -20px;
          margin-top: -10px;
        }

        .testimonial-text {
          color: #374151;
          line-height: 1.75;
          font-size: 15px;
          font-style: italic;
          margin: 0 0 24px 0;
        }

        .testimonial-footer {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 14px;
        }

        .testimonial-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #ff8c00, #ffcc44);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          flex-shrink: 0;
        }

        .testimonial-name {
          font-weight: 700;
          color: #1a1a2e;
          font-size: 15px;
        }

        .testimonial-role { color: #9ca3af; font-size: 13px; }
        .star-row { font-size: 14px; letter-spacing: 2px; }

        /* CTA Banner */
        .cta-section {
          background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
          padding: 100px 5%;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .cta-section::before {
          content: '';
          position: absolute;
          inset: -50%;
          background: radial-gradient(ellipse at center, rgba(255,140,0,0.12) 0%, transparent 60%);
          animation: rotateSlow 20s linear infinite;
        }

        .cta-title {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(30px, 4vw, 52px);
          font-weight: 800;
          color: #fff;
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
        }

        .cta-subtitle {
          font-size: 18px;
          color: rgba(255,255,255,0.7);
          max-width: 550px;
          line-height: 1.7;
          margin-bottom: 40px;
          position: relative;
          z-index: 1;
        }

        /* Footer */
        .footer {
          background: #0a0818;
          color: rgba(255,255,255,0.5);
          padding: 40px 5%;
          text-align: center;
          font-size: 14px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }

        .footer-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 16px;
        }

        .footer-links {
          display: flex;
          justify-content: center;
          gap: 30px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .footer-link {
          color: rgba(255,255,255,0.4);
          text-decoration: none;
          transition: color 0.2s;
          font-size: 13px;
        }

        .footer-link:hover { color: #ff8c00; }

        @media (max-width: 768px) {
          .hero-content { grid-template-columns: 1fr; gap: 40px; padding: 100px 5% 60px; }
          .hero-image-wrap { order: -1; }
          .steps-grid, .features-grid, .testimonials-grid { grid-template-columns: 1fr; }
          .steps-grid::before { display: none; }
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
          .gallery-inner { grid-template-columns: 1fr; }
          .hero-badge-float.top-left { left: 10px; top: 10px; }
          .hero-badge-float.bottom-right { right: 10px; bottom: 10px; }
        }
      `}</style>

      {/* Navbar */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} role="navigation">
        <div className="navbar-inner">
          <div className="logo">
            <div className="logo-icon-wrap">🍱</div>
            <span className="logo-text">MealForWeek</span>
          </div>
          <button id="nav-start-btn" className="nav-cta" onClick={onStart}>
            Start Planning <span style={{ fontSize: '18px' }}>→</span>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-grid-pattern" />
        <div className="hero-overlay" />

        {/* Floating food emojis */}
        {floatingItems.map((item, i) => (
          <FloatingEmoji key={i} emoji={item.emoji} style={item.style} />
        ))}

        <div className="hero-content">
          {/* Left Text */}
          <div>
            <div className="hero-badge">
              ✨ AI-Powered • Family-Focused
            </div>
            <h1 className="hero-title">
              Healthy, <span className="highlight">Kid-Approved</span>{' '}
              Meals Planned in Minutes
            </h1>
            <p className="hero-subtitle">
              For busy parents who want to nourish their families without the daily stress.
              Our AI crafts delicious weekly menus tailored to your family's tastes.
            </p>
            <div className="hero-actions">
              <button id="hero-cta-primary" className="cta-primary" onClick={onStart}>
                🚀 Create Your Menu
              </button>
              <button className="cta-secondary" onClick={onStart}>
                ▶ See How It Works
              </button>
            </div>
            <div className="hero-trust">
              <div className="trust-avatars">
                {['👩', '👨', '👩‍🦱', '👨‍🦳', '👩‍🦰'].map((a, i) => (
                  <div key={i} className="trust-avatar">{a}</div>
                ))}
              </div>
              <span>Loved by <strong style={{ color: 'rgba(255,255,255,0.8)' }}>2,500+</strong> families worldwide</span>
            </div>
          </div>

          {/* Right Image */}
          <div className="hero-image-wrap">
            <div className="hero-image-card">
              <img src="/family_cooking_hero.png" alt="Happy family cooking together in a bright kitchen" />
              <div className="hero-image-overlay" />
            </div>
            <div className="hero-badge-float top-left">
              <div className="badge-dot" />
              <div>
                <div style={{ fontSize: 15, fontWeight: 800 }}>7-Day Plan Ready</div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>AI built it in 3 sec ⚡</div>
              </div>
            </div>
            <div className="hero-badge-float bottom-right">
              <span style={{ fontSize: 22 }}>🥗</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800 }}>35 Healthy Recipes</div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Kid-approved picks!</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="stats-section">
        <div className="stats-grid">
          <StatCard value={2500} label="Happy Families" icon="👨‍👩‍👧‍👦" delay="0s" />
          <StatCard value={98} label="Picky Eater Approval" icon="🧒" delay="0.1s" />
          <StatCard value={15000} label="Recipes Generated" icon="🍽️" delay="0.2s" />
          <StatCard value={4} label="Hours Saved Weekly" icon="⏱️" delay="0.3s" />
        </div>
      </section>

      {/* How It Works */}
      <section className="how-section">
        <div className="section-eyebrow">Simple Process</div>
        <h2 className="section-title">How It Works in 3 Steps</h2>
        <p className="section-subtitle">
          From ingredients in your fridge to a full week of meals — in under 60 seconds.
        </p>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number-wrap">🥕</div>
            <div className="step-title">Tell Us What You Have</div>
            <p className="step-desc">Simply enter the ingredients you have at home. No more wasted food or last-minute grocery runs!</p>
            <span className="step-tag">Step 1</span>
          </div>
          <div className="step-card">
            <div className="step-number-wrap">🤖</div>
            <div className="step-title">AI Recipe Magic</div>
            <p className="step-desc">Our Gemini AI generates a curated list of delicious, kid-friendly recipes tailored just for your family.</p>
            <span className="step-tag">Step 2</span>
          </div>
          <div className="step-card">
            <div className="step-number-wrap">📅</div>
            <div className="step-title">Plan Your Week</div>
            <p className="step-desc">Drag and drop recipes into your weekly calendar. Print it, stick it on the fridge — done!</p>
            <span className="step-tag">Step 3</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="section-eyebrow">Why Families Love Us</div>
        <h2 className="section-title">Everything You Need to Feed Your Family Well</h2>
        <p className="section-subtitle" style={{ marginBottom: '60px' }}>
          Smart features built for real, busy families — not just meal-planning enthusiasts.
        </p>
        <div className="features-grid">
          {[
            { icon: '⏱️', bg: '#e8f8f5', title: 'Save Hours Every Week', desc: "No more wondering \"what's for dinner?\". Get your whole week planned in minutes and reclaim your evenings.", },
            { icon: '🥪', bg: '#fef5e7', title: 'Picky-Eater Approved', desc: "AI finds meals your kids will actually ask for seconds. Nutritious, colorful, and genuinely delicious.", },
            { icon: '🛒', bg: '#eaf2f8', title: 'Smart Shopping List', desc: "Auto-generate a consolidated grocery list from your weekly menu. Never forget an ingredient again.", },
            { icon: '🖨️', bg: '#f3e8ff', title: 'Print & Fridge-Ready', desc: "One-click print your weekly menu. Stick it on the fridge so the whole family knows what's coming.", },
            { icon: '🌱', bg: '#ecfdf5', title: 'Nutrition-Balanced', desc: "AI ensures each week has a healthy mix of proteins, veggies, grains, and treats — in balance.", },
            { icon: '🔄', bg: '#fef2f2', title: 'Flexible & Customizable', desc: "Swap out meals you don't like, add dietary restrictions, and regenerate in one click.", },
          ].map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon-wrap" style={{ background: f.bg }}>
                {f.icon}
              </div>
              <div className="feature-title">{f.title}</div>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="gallery-inner">
          <div className="gallery-text">
            <div className="section-eyebrow" style={{ color: '#ffb347' }}>Real Families, Real Meals</div>
            <h2 className="section-title" style={{ color: '#fff', textAlign: 'left', marginBottom: '20px' }}>
              Bring Joy Back to the Dinner Table
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.8', fontSize: '17px', marginBottom: '32px' }}>
              Cooking together as a family creates lasting memories. Our planner gives you the gift of time — 
              less stress planning, more moments laughing and cooking together.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {['🕒 15 min avg setup', '🤖 Gemini AI powered', '🥦 100% family-friendly'].map((badge, i) => (
                <span key={i} style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: 'rgba(255,255,255,0.85)',
                  padding: '8px 16px',
                  borderRadius: '50px',
                  fontSize: '13px',
                  fontWeight: '600',
                }}>
                  {badge}
                </span>
              ))}
            </div>
          </div>
          <div className="gallery-image-wrap">
            <img src="/meal_plan_table.png" alt="Colorful weekly meal plan laid out beautifully on a table" />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-eyebrow">Stories from Families</div>
        <h2 className="section-title">Parents Are Loving It</h2>
        <p className="section-subtitle" style={{ marginBottom: '60px' }}>
          Don't just take our word for it — hear what real families are saying.
        </p>
        <div className="testimonials-grid">
          <TestimonialCard
            quote="This app has completely transformed our weeknight routine. I used to spend 30 minutes just deciding what to cook. Now I have a full week planned in seconds!"
            name="Sarah M."
            role="Mom of 2 · Chicago"
            avatar="👩"
            delay="0s"
          />
          <TestimonialCard
            quote="My kids are notoriously picky eaters. The AI somehow always finds meals they love. Our dinner time stress has dropped to zero. Absolute game changer."
            name="James & Lisa K."
            role="Parents of 3 · Austin"
            avatar="👨"
            delay="0.1s"
          />
          <TestimonialCard
            quote="I was skeptical about the AI part, but it genuinely understands what a busy parent needs. Nutritious meals, minimal effort, happy kids. What more could you want?"
            name="Priya R."
            role="Working Mom · Seattle"
            avatar="👩‍🦱"
            delay="0.2s"
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section">
        <h2 className="cta-title">Ready to Make Dinner Effortless?</h2>
        <p className="cta-subtitle">
          Join thousands of families who've already said goodbye to the "what's for dinner?" panic.
        </p>
        <button id="footer-cta-btn" className="cta-primary" onClick={onStart} style={{ position: 'relative', zIndex: 1 }}>
          🍽️ Start Planning for Free
        </button>
        <p style={{ color: 'rgba(255,255,255,0.35)', marginTop: '20px', fontSize: '13px', position: 'relative', zIndex: 1 }}>
          No account needed • Free to use • Powered by Google Gemini AI
        </p>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-logo">
          <div style={{ fontSize: '22px' }}>🍱</div>
          <span style={{ fontFamily: 'Poppins', fontWeight: 800, color: 'rgba(255,255,255,0.7)', fontSize: '17px' }}>MealForWeek</span>
        </div>
        <div className="footer-links">
          <a href="#!" className="footer-link">Privacy Policy</a>
          <a href="#!" className="footer-link">Terms of Service</a>
          <a href="#!" className="footer-link">Contact</a>
          <a href="#!" className="footer-link">About</a>
        </div>
        <p style={{ margin: 0 }}>© {new Date().getFullYear()} MealForWeek AI Planner. Made with ❤️ for families everywhere.</p>
      </footer>
    </div>
  );
}

export default LandingPage;
